import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast, ToastContainer } from 'react-toastify';
import classNames from 'classnames';

import * as FieldType from '../FieldBuilder/FieldConstants';
import InfoModal from '../../Modal/InfoModal';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';
import TenantContext from '../../../hoc/Context/TenantContext';
import GroupBuilder from './GroupBuilder';
import { buildFormBuilderFields, isValid } from './FormBuilderUtils';
import 'react-toastify/dist/ReactToastify.css';
import DragnDrop from '../../Upload/DragnDrop';
import PrivacyPolicy from './PrivacyPolicy';

/**
 * Build form from config.
 */
class FormBuilder extends Component {
  static contextType = TenantContext;

  constructor(props) {
    super(props);
    this.state = {
      fields: buildFormBuilderFields(props.config.groups, props.fields),
      mediaFiles: [],
      showValidation: false,
      loaded: false,
      privacyPolicies: []
    };
  }

  createListFields = (field, index) => {
    const { intl } = this.props;

    const options = field.list.map(item => {
      return {
        key: item,
        value: intl.formatMessage({
          id: `form.field.${field.id}.option.${item}`,
          defaultMessage: item
        })
      };
    });

    return Promise.resolve({
      group: index,
      field: field.id,
      list: options
    });
  }

  /**
   * Load values of list fields.
   */
  componentDidMount() {
    const { errorHandler, config, fields, huntekaConfig } = this.props;

    if (huntekaConfig && huntekaConfig.policyCheckBoxTexts.length !== 0) {
      const privacyPolicies = huntekaConfig.policyCheckBoxTexts.map((policy, index) => {
        return { [`privacyPolicy_${index}`]: false };
      });
      this.setState({ privacyPolicies });
    }

    const listFields = config.groups
      .map((group, index) => {
        return group.fields
          .map(field => {
            return { ...fields[field], id: field };
          })
          .filter(key => {
            return key.type === FieldType.LIST_FIELD;
          })
          .map(field => {
            if (field.query) {
              const optionsList = this.loadOptionsList(index, field);

              if (field.listPath === 'contactLanguages') {
                optionsList.then(result => {
                  if (!result) {
                    return this.createListFields(field, index);
                  }
                });
              }

              return optionsList;
            } else {
              return this.createListFields(field, index);
            }
          });
      })
      .reduce((result, fields) => {
        fields.forEach(field => {
          result.push(field);
        });
        return result;
      }, []);

    Promise.all(listFields)
      .then(responses => {
        const stateFields = [...this.state.fields];
        responses.forEach(item => {
          stateFields[item.group][item.field].list = item.list;
          stateFields[item.group][item.field].sublist = item.sublist;
        });
        this.setState({
          fields: stateFields,
          loaded: true
        });
      })
      .catch(ex => {
        errorHandler(ex);
      });
  }

  loadOptionsList = (index, field) => {
    const { tenant, locale } = this.context;
    const { query, listKey, listValue, sublist, post, body, visibility } = field;

    const options = {};
    if (post) {
      options.method = 'POST';
      options.headers = {
        'Content-Type': 'application/json'
      };
      if (body) {
        options.body = JSON.stringify(body);
      }
    }

    return fetch(
      `${query}?tenant_id=${tenant}&language=${locale.language}_${locale.country}`,
      options
    )
      .then(res => {
        if (res.status !== 200) {
          throw new Error('Bad response from server');
        }
        return res.json();
      })
      .then(list => {
        const optionList = [];
        this.getListFromPath(field.listPath, list).forEach(listItem => {
          if (sublist) {
            sublist[listItem[listKey]] = this.getListFromPath(sublist.listPath, listItem)
              .map(sublistItem => {
                if (sublist.visibility ? sublistItem[sublist.visibility] : true) {
                  return {
                    key: sublistItem[sublist.listKey],
                    value: sublistItem[sublist.listValue]
                  };
                } else {
                  return null;
                }
              })
              .filter(Boolean);
          }
          if (
            visibility
              ? listItem[visibility]
              : true && sublist
              ? sublist[listItem[listKey]].length > 0
              : true
          ) {
            optionList.push({
              key: listItem[listKey],
              value: listItem[listValue]
            });
          }
        });

        return {
          group: index,
          field: field.id,
          sublist: sublist,
          list: optionList
        };
      }).catch(error => {
        console.error('Failed to laod options. ', error);
      });
  };

  getListFromPath = (listPath, list) => {
    return listPath
      ? listPath.split('.').reduce((result, path) => {
          return result[path];
        }, list)
      : list;
  };

  updatePrivacyPoliciesState = checkboxIndex => {
    const privacyPolicies = [...this.state.privacyPolicies];
    const privacyPolicy = { ...privacyPolicies[checkboxIndex] };
    privacyPolicy[`privacyPolicy_${checkboxIndex}`] =
      !privacyPolicy[`privacyPolicy_${checkboxIndex}`];
    privacyPolicies[checkboxIndex] = privacyPolicy;
    this.setState({ privacyPolicies });
  };

  validatePrivacyPolicies = () => {
    if (this.state.privacyPolicies.length === 0) {
      return undefined;
    }
    for (let i = 0; i < this.state.privacyPolicies.length; i++) {
      let privacyPolicies = this.state.privacyPolicies;
      const checkboxError = document.getElementById('checkboxError_' + i);
      if (privacyPolicies[i][`privacyPolicy_${i}`] === false) {
        checkboxError.innerHTML = this.props.intl.formatMessage({
          id: `form.field.privacyPolicy.error`
        });
        return false;
      } else {
        checkboxError.innerHTML = '&nbsp;';
      }
    }
  };

  submit = event => {
    event.preventDefault();

    const validCheckboxes = this.validatePrivacyPolicies;

    const { intl } = this.props;

    const validForm = Object.keys(this.state.fields)
      .map(group => {
        return Object.keys(this.state.fields[group])
          .map(item => {
            return this.state.fields[group][item].valid;
          })
          .reduce((valid, result) => {
            return result && valid;
          }, true);
      })
      .reduce((valid, result) => {
        return result && valid;
      }, true);
    if (validForm && validCheckboxes() === undefined && this.state.invalidRecaptcha !== undefined) {
      this.sendRegistration();
    } else {
      this.showErrorToast(intl.formatMessage({ id: 'form.error.incompleteform.message' }));
      this.setState({
        showValidation: true
      });
    }
  };

  upladMediaFiles = () => {
    const { tenant, locale } = this.context;
    const { mediaFiles } = this.state;
    const registrationData = this.getRegistrationData();
    const descriptor = registrationData.onlineRegistrationDescriptor;

    if (
      mediaFiles.length !== 0 &&
      descriptor.email &&
      descriptor.servicePointId &&
      descriptor.servicePlaceId
    ) {
      const uploadData = new FormData();
      mediaFiles.forEach(file => {
        uploadData.append('files', file, file.path);
      });
      uploadData.append('email', descriptor.email);
      uploadData.append('language', `${locale.language}-${locale.country}`);
      uploadData.append('servicePointId', descriptor.servicePointId);
      uploadData.append('servicePlaceId', descriptor.servicePlaceId);
      fetch(`${this.props.config.destination}/upload?tenant_id=${tenant}`, {
        method: 'POST',
        body: uploadData
      }).catch(error => {
        console.error(error);
      });
    }
  };

  sendRegistration = () => {
    const { tenant } = this.context;
    const { intl } = this.props;
    const registrationData = this.getRegistrationData();

    this.setState({
      loaded: false
    });

    this.validateReCAPTCHA()
      .then(() =>
        fetch(`${this.props.config.destination}?tenant_id=${tenant}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(registrationData)
        })
      )
      .then(res => (res.status >= 400 ? Promise.reject(res) : res.json()))
      .then(response => {
        if (response.emailOk) {
          this.afterSuccessfulRegistration();
        } else {
          this.handleFaultyEmail(response.printLine);
        }
      })
      .catch(error => {
        error.json().then(e => {
          console.warn(e);
          if (e.hasOwnProperty('message')) {
            this.showErrorToast(e.message);
          } else {
            this.showErrorToast(intl.formatMessage({ id: 'form.error.fatal.message' }));
          }
        });
        this.resetCaptcha();
        this.setState({ loaded: true });
      });
  };

  afterSuccessfulRegistration = () => {
    this.upladMediaFiles();
    this.setState({
      successRegistration: true,
      loaded: true
    });
  };

  handleFaultyEmail = message => {
    this.resetCaptcha();
    this.setState({ loaded: true });
    const toastErrorMessage = this.checkForExistingEmail(message);
    this.showErrorToast(toastErrorMessage);
  };

  checkForExistingEmail = message => {
    const { intl } = this.props;
    return (
      <div>
        {intl.formatMessage({ id: `form.error.${message}.message` })}&nbsp;
        {message === 'EMAIL_EXISTS' && (
          <>
            {intl.formatMessage(
              { id: 'form.error.EMAIL_EXISTS.forgottenMessage' },
              {
                a: chunks => (
                  <a target="_blank" rel="noreferrer" href={`${window.origin}?to-login=true`}>
                    {chunks}
                  </a>
                ),

                b: chunks => (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`${window.origin}?to-forgotten-password=true`}
                  >
                    {chunks}
                  </a>
                )
              }
            )}
          </>
        )}
      </div>
    );
  };

  showErrorToast = msg => {
    toast.error(msg, {
      position: 'bottom-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined
    });
  };

  getRegistrationData = () => {
    const { tenant, locale } = this.context;
    const onlineRegistrationDescriptor = this.state.fields
      .map(group => {
        return Object.keys(group)
          .map(key => {
            return {
              key: group[key].formField || key,
              value: group[key].value
            };
          })
          .reduce((results, field) => {
            results.push(field);
            return results;
          }, []);
      })
      .reduce((results, fieldGroups) => {
        fieldGroups.forEach(item => {
          results[item.key] = item.value;
        });
        return results;
      }, {});

    const response = {
      institutionId: '1',
      language: `${locale.language}-${locale.country}`,
      onlineRegistrationDescriptor: onlineRegistrationDescriptor
    };
    response.onlineRegistrationDescriptor.activationUrl = `${window.location.origin}${window.location.pathname}#/activation/${tenant}`;
    return response;
  };

  validateReCAPTCHA = () => {
    const { tenant } = this.context;
    return fetch(
      `/circulation-proxy/captcha?captchaResponse=${this.state.recaptchaToken}&tenant_id=${tenant}`
    )
      .then(response => {
        return response.json();
      })
      .then(data => {
        if (data.status !== 200) {
          return Promise.reject();
        }
      });
  };

  onRecaptchaChange = recaptchaToken => {
    this.setState({
      recaptchaToken: recaptchaToken,
      invalidRecaptcha: false
    });
  };

  resetCaptcha = () => {
    this.captcha.reset();
    this.setState({
      recaptchaToken: '',
      invalidRecaptcha: true
    });
  };

  changeField = (group, fieldId, event) => {
    const originalFieldId = fieldId.startsWith('_') ? fieldId.substr(1) : fieldId;
    const sameAsFieldId = `_${originalFieldId}`;
    const stateFields = [...this.state.fields];
    const stateGroup = { ...stateFields[group] };
    let newValue = event.target.value;
    if (stateGroup[originalFieldId].checkbox) {
      newValue = event.target.checked ? true : null;
    }
    if (stateGroup[originalFieldId].child) {
      const childField = stateGroup[stateGroup[originalFieldId].child];
      stateGroup[stateGroup[originalFieldId].child].list =
        stateGroup[originalFieldId].sublist[newValue];
      stateGroup[stateGroup[originalFieldId].child] = this.updateField(childField, '');
    }
    if (stateGroup[originalFieldId].sameas) {
      if (fieldId.startsWith('_')) {
        stateGroup[sameAsFieldId] = this.updateField(stateGroup[sameAsFieldId], newValue);
        stateGroup[originalFieldId] = this.updateField(
          stateGroup[originalFieldId],
          stateGroup[originalFieldId].value
        );
      } else {
        stateGroup[originalFieldId] = this.updateField(stateGroup[originalFieldId], newValue);
        stateGroup[sameAsFieldId] = this.updateField(
          stateGroup[sameAsFieldId],
          stateGroup[sameAsFieldId].value
        );
      }
      if (stateGroup[sameAsFieldId].valid) {
        stateGroup[sameAsFieldId].valid =
          stateGroup[sameAsFieldId].value === stateGroup[originalFieldId].value;
      }
    } else {
      stateGroup[fieldId] = this.updateField(stateGroup[fieldId], newValue);
    }
    stateFields[group] = stateGroup;
    this.setState({
      fields: stateFields
    });
  };

  updateField = (field, value) => {
    const stateField = { ...field, value };
    stateField.valid = isValid(stateField);
    return stateField;
  };

  closeSuccessRegistration = () => {
    window.location = '/';
  };

  addAcceptedFiles = files => {
    this.setState({
      mediaFiles: files
    });
  };

  updateUserDetails = userDetails => {
    const {
      fields: [personalInformations, ...remainingFields]
    } = this.state;

    const updatedPersonalInformations = Object.entries(personalInformations).reduce(
      (result, [fiedName, fieldValue]) => {
        result[fiedName] = this.updateField(fieldValue, userDetails[fiedName] || '');
        return result;
      },
      {}
    );

    this.setState({
      fields: [updatedPersonalInformations, ...remainingFields]
    });
  };

  render() {
    const { locale } = this.context;
    const { loaded, fields, showValidation, successRegistration } = this.state;
    const {
      config,
      fields: baseFields,
      recaptchaSiteKey,
      fileUploadLimitations,
      huntekaConfig = {}
    } = this.props;
    return (
      <div className={classNames({ 'embedded-loading-indicator': !loaded })}>
        <Form noValidate onSubmit={this.submit}>
          {config.title && <h2 className="text-center">{config.title}</h2>}
          <GroupBuilder
            groupConfigs={config.groups}
            fieldConfigs={baseFields}
            fieldValues={fields}
            showValidation={showValidation}
            changeField={this.changeField}
            onUserDetails={this.updateUserDetails}
          />
          {huntekaConfig.policyCheckBoxTexts?.map((privacyPolicy, index) => (
            <PrivacyPolicy
              key={index}
              label={privacyPolicy}
              index={index}
              intl={this.props.intl}
              updatePrivacyPoliciesState={this.updatePrivacyPoliciesState}
            />
          ))}
          {huntekaConfig.allowedFields?.includes('upload') ? (
            <DragnDrop
              fileUploadLimitations={fileUploadLimitations}
              onAcceptedFileDrag={this.addAcceptedFiles}
            />
          ) : null}
          {recaptchaSiteKey && (
            <div className={`google-recaptcha text-center${this.state.invalidRecaptcha === undefined ? ' recaptcha-error' : ''}`}>
              <ReCAPTCHA
                sitekey={recaptchaSiteKey}
                ref={e => (this.captcha = e)}
                hl={locale.language}
                onExpired={this.resetCaptcha}
                onChange={this.onRecaptchaChange}
              />
              <div className='recaptcha-error-container'>
                {this.state.invalidRecaptcha === undefined && this.state.showValidation && 
                  <Form.Text className="error-message-small text-danger">
                    <FormattedMessage id={`form.field.recaptcha.error`} />
                  </Form.Text>}
              </div>
            </div>
          )}
          <div className="text-center form-submit-button">
            <Button variant="primary" type="submit">
              <FormattedMessage id={`button.${this.props.config.button}`} />
            </Button>
          </div>
          {!loaded && <LoadingIndicator />}
        </Form>
        {successRegistration && (
          <InfoModal
            classNames={classNames({
              accessibleModal: sessionStorage.getItem('accessibleView') === 'true'
            })}
            title={<FormattedMessage id="form.success.title" />}
            show={true}
            close={this.closeSuccessRegistration}
          >
            <div>
              <FormattedMessage id="form.success.message" />
            </div>
          </InfoModal>
        )}
        <ToastContainer />
      </div>
    );
  }
}

export default injectIntl(FormBuilder);
